import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Paper, Checkbox, Chip, IconButton } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import { Table, Modal, Button } from "../../components";
import { CLIENT_ACTION } from "../../redux/action";
import { Toaster, getUserRole } from "../../utils/common";
import ClientBusinesses from "../client_businesses";
import "./businessesAdmin.scss";

function clientDisplayName(client) {
  if (!client) return "—";
  const name = `${client.first_name || ""} ${client.last_name || ""}`.trim();
  return name || client.email || client.client_id || "—";
}

function BusinessesAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [assignOpen, setAssignOpen] = useState(false);
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [assignable, setAssignable] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const skipAssignSearchFetchRef = useRef(false);

  const loadBusinesses = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const data = await CLIENT_ACTION.getAllBusinesses({ search: q });
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBusinesses("");
  }, [loadBusinesses]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 2000);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    loadBusinesses(search);
  }, [search, loadBusinesses]);

  const loadAssignable = useCallback(async (businessId, q = "") => {
    if (!businessId) return;
    setAssignLoading(true);
    try {
      const list = await CLIENT_ACTION.getAssignableBusinessClients(
        businessId,
        q,
      );
      setAssignable(Array.isArray(list) ? list : []);
    } finally {
      setAssignLoading(false);
    }
  }, []);

  const openAssign = (business) => {
    // openAssign loads once; keep skip true so open-triggered effects do not re-fetch
    skipAssignSearchFetchRef.current = true;
    setActiveBusiness(business);
    setSelectedIds([]);
    setAssignSearch("");
    setAssignOpen(true);
    loadAssignable(business.client_business_id, "");
  };

  useEffect(() => {
    if (!assignOpen || !activeBusiness?.client_business_id) return;
    if (skipAssignSearchFetchRef.current) return;
    const t = setTimeout(() => {
      loadAssignable(activeBusiness.client_business_id, assignSearch.trim());
    }, 2000);
    return () => clearTimeout(t);
  }, [assignSearch, assignOpen, activeBusiness, loadAssignable]);

  const toggleSelect = (clientId) => {
    setSelectedIds((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId],
    );
  };

  const handleAssign = async () => {
    if (!activeBusiness?.client_business_id || !selectedIds.length) {
      Toaster.warning("Select at least one client to assign");
      return;
    }
    setSaving(true);
    try {
      const result = await CLIENT_ACTION.assignBusinessMembers({
        client_business_id: activeBusiness.client_business_id,
        client_ids: selectedIds,
      });
      if (result?.status === 1) {
        Toaster.success(result.message || "Clients assigned");
        setAssignOpen(false);
        await loadBusinesses(search);
      } else {
        Toaster.error(result?.message || "Failed to assign clients");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (business, memberClientId) => {
    if (!business?.client_business_id || !memberClientId) return;
    const result = await CLIENT_ACTION.removeBusinessMember({
      client_business_id: business.client_business_id,
      client_id: memberClientId,
    });
    if (result?.status === 1) {
      Toaster.success("Client removed");
      await loadBusinesses(search);
    } else {
      Toaster.error(result?.message || "Failed to remove client");
    }
  };

  const tableHeaders = useMemo(
    () => [
      { id: "business_name", label: "Business name" },
      { id: "business_type", label: "Type" },
      { id: "owner_name", label: "Owner client" },
      { id: "owner_email", label: "Owner email" },
      { id: "members_display", label: "Assigned clients" },
      { id: "actions", label: "Actions" },
    ],
    [],
  );

  const tableData = useMemo(
    () =>
      rows.map((row) => {
        const owner = row.owner;
        const members = Array.isArray(row.members) ? row.members : [];
        return {
          business_name: row.business_name || "—",
          business_type: row.business_type || "—",
          owner_name: clientDisplayName(owner),
          owner_email: owner?.email || "—",
          members_display: (
            <div className="businesses-members-cell">
              {members.length === 0 ? (
                <span className="businesses-muted">No assigned clients</span>
              ) : (
                members.map((m) => (
                  <Chip
                    key={m.client_id}
                    size="small"
                    label={clientDisplayName(m.client)}
                    onDelete={() => handleRemoveMember(row, m.client_id)}
                    className="businesses-member-chip"
                  />
                ))
              )}
            </div>
          ),
          actions: (
            <div className="businesses-assign-btn-wrap">
              <Button
                text="Assign clients"
                handleClick={() => openAssign(row)}
              />
            </div>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows],
  );

  return (
    <div className="businesses-admin-page">
      <Paper className="businesses-admin-toolbar" elevation={0}>
        <div className="businesses-admin-toolbar-left">
          <p className="businesses-admin-subtitle">
            Assign additional business clients so they can view and upload
            shared business documents.
          </p>
        </div>
      </Paper>

      <Paper className="businesses-admin-table-wrap" elevation={0}>
        <div className="businesses-admin-table-toolbar">
          <div className="businesses-admin-search">
            <SearchIcon className="businesses-admin-search-icon" />
            <input
              type="text"
              placeholder="Search businesses…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
        {loading ? (
          <div className="businesses-admin-empty">Loading businesses…</div>
        ) : (
          <Table
            title=""
            header={tableHeaders}
            data={tableData}
            showFilter={false}
            variant="small"
            aligncss="dashboard-table"
          />
        )}
      </Paper>

      <Modal
        title={null}
        open={assignOpen}
        handleClose={() => !saving && setAssignOpen(false)}
        className="businesses-assign-modal-dialog"
      >
        <div className="businesses-assign-modal-shell">
          <div className="businesses-assign-modal-header">
            <h3 className="businesses-assign-modal-title">
              {activeBusiness
                ? `Assign clients - ${activeBusiness.business_name}`
                : "Assign clients"}
            </h3>
            <IconButton
              aria-label="close"
              className="businesses-assign-modal-close"
              onClick={() => !saving && setAssignOpen(false)}
              disabled={saving}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className="businesses-admin-search businesses-assign-search">
            <SearchIcon className="businesses-admin-search-icon" />
            <input
              type="text"
              placeholder="Search business clients"
              value={assignSearch}
              onChange={(e) => {
                skipAssignSearchFetchRef.current = false;
                setAssignSearch(e.target.value);
              }}
            />
          </div>

          <div className="businesses-assign-list">
            {assignLoading ? (
              <div className="businesses-admin-empty">Loading clients…</div>
            ) : assignable.length === 0 ? (
              <div className="businesses-admin-empty">
                No assignable business clients found
              </div>
            ) : (
              assignable.map((client) => {
                const checked = selectedIds.includes(client.client_id);
                return (
                  <label
                    key={client.client_id}
                    className={`businesses-assign-row${
                      checked ? " is-selected" : ""
                    }`}
                  >
                    <Checkbox
                      color="primary"
                      checked={checked}
                      onChange={() => toggleSelect(client.client_id)}
                    />
                    <span className="businesses-assign-row-text">
                      <strong>{clientDisplayName(client)}</strong>
                      <span>{client.email || ""}</span>
                      {client.business_name ? (
                        <span className="businesses-muted">
                          {client.business_name}
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })
            )}
          </div>

          <div className="businesses-assign-actions">
            <Button
              text="Cancel"
              ui="transparent"
              variant="outline"
              disable={saving}
              handleClick={() => setAssignOpen(false)}
            />
            <Button
              text={saving ? "Assigning…" : "Assign selected"}
              disable={saving || !selectedIds.length}
              handleClick={handleAssign}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

function BusinessesPage() {
  if (getUserRole() === "client") {
    return <ClientBusinesses />;
  }
  return <BusinessesAdmin />;
}

export default BusinessesPage;
